// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { FlyoverDistances } from './useFlyoverVideos';

/**
 * What the flyover's distance readout says at one moment: a number of metres, and which end
 * of the hole it is measured from.
 */
export type FlyoverDistanceReading = {
  meters: number;
  measuredFrom: 'tee' | 'green';
};

/**
 * The distance the camera is at, `progress` of the way through the video.
 *
 * The samples come from the renderer, evenly spread across the video, so this only has to
 * find the pair `progress` falls between and interpolate — the flight's easing and the bends
 * it follows are already in the numbers. Nothing here re-derives them.
 *
 * Which end it counts from follows what the video itself paints (see `draw_distance_markers`
 * in the analyser): on a par 4 or 5 the first half of the hole counts *up* from the tee,
 * because off the tee the distance a player thinks in is how far they have hit it, and the
 * second half counts *down* to the green. A par 3, or a hole whose par we do not know, is
 * one shot to the green and counts down throughout.
 *
 * Before the tee — the run-up the camera flies in on — there is no distance worth showing,
 * so this reads 0 from the tee rather than a negative number.
 */
export const readFlyoverDistance = (
  distances: FlyoverDistances,
  progress: number,
  par: number | null
): FlyoverDistanceReading | null => {
  const samples = distances.toGreenM;
  if (samples.length < 2) return null;

  const at = Math.min(1, Math.max(0, progress)) * (samples.length - 1);
  const lower = Math.floor(at);
  const upper = Math.min(samples.length - 1, lower + 1);
  const toGreen = samples[lower] + (samples[upper] - samples[lower]) * (at - lower);

  const fromTee = distances.holeLengthM - toGreen;
  const countsFromTee = (par === 4 || par === 5) && fromTee < distances.holeLengthM / 2;
  return countsFromTee
    ? { meters: Math.max(0, Math.round(fromTee)), measuredFrom: 'tee' }
    : { meters: Math.max(0, Math.round(toGreen)), measuredFrom: 'green' };
};
